import { useEffect, useMemo, useRef, useState } from "react";

// ─── shared ───────────────────────────────────────────────────────────────────

function FilterLabel({ children }) {
  return (
    <span className="text-[11px] font-semibold uppercase tracking-wider text-palette-gray-secondary mb-1 block">
      {children}
    </span>
  );
}

function TriggerBtn({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center justify-between gap-2 h-9 px-3 rounded-lg border border-palette-gray-divider bg-white text-sm font-medium text-palette-gray-heading hover:bg-palette-gray-subtle transition-colors min-w-[160px]"
    >
      {children}
    </button>
  );
}

function Caret({ open }) {
  return (
    <svg
      className={`w-3.5 h-3.5 text-palette-gray-secondary shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
      viewBox="0 0 10 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M1 1l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── MonthFilter ──────────────────────────────────────────────────────────────

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// `multiple`: clicks toggle months on/off (at least one stays selected) and the
// panel stays open; otherwise a click picks one month and closes it.
function MonthFilter({ months, onMonthsChange, disableCurrentAndFuture, multiple = false }) {
  const month = months[0] ?? "";
  const now = useMemo(() => new Date(), []);
  const selectedYear = month ? Number(month.slice(0, 4)) : now.getFullYear();
  const selectedMonth = month ? Number(month.slice(5, 7)) : 0;

  const [open, setPanelOpen] = useState(false);
  const [panelYear, setPanelYear] = useState(selectedYear);
  const ref = useRef(null);

  useEffect(() => {
    setPanelYear(selectedYear);
  }, [selectedYear]);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setPanelOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const monthLabel = (ym) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
  const sortedMonths = [...months].sort();
  const label = !month
    ? "Select month"
    : sortedMonths.length === 1
      ? monthLabel(month)
      : sortedMonths.length === 2
        ? sortedMonths.map(monthLabel).join(", ")
        : `${sortedMonths.length} months (${monthLabel(sortedMonths[0])} – ${monthLabel(
            sortedMonths[sortedMonths.length - 1],
          )})`;

  function isDisabled(year, m) {
    if (!disableCurrentAndFuture) return false;
    const cur = now.getMonth() + 1;
    const curY = now.getFullYear();
    if (year > curY) return true;
    if (year === curY && m > cur) return true;
    if (year === curY && m === cur) return now.getDate() < 25;
    return false;
  }

  function select(m) {
    const mm = String(m).padStart(2, "0");
    if (isDisabled(panelYear, m)) return;
    const key = `${panelYear}-${mm}`;
    if (multiple) {
      if (!months.includes(key)) onMonthsChange([...months, key].sort());
      else if (months.length > 1) onMonthsChange(months.filter((v) => v !== key));
      return;
    }
    onMonthsChange([key]);
    setPanelOpen(false);
  }

  const canNext = !disableCurrentAndFuture || panelYear < now.getFullYear();

  return (
    <div className="relative" ref={ref}>
      <FilterLabel>Month</FilterLabel>
      <TriggerBtn onClick={() => setPanelOpen((v) => !v)}>
        <span className="truncate">{label}</span>
        <Caret open={open} />
      </TriggerBtn>

      {open && (
        <div className="absolute z-50 mt-1 w-64 rounded-xl border border-palette-gray-divider bg-white shadow-xl p-3">
          {/* Year nav */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setPanelYear((y) => y - 1)}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-palette-gray-subtle text-palette-gray-secondary"
            >
              ◀
            </button>
            <span className="text-sm font-bold text-palette-gray-primary">{panelYear}</span>
            <button
              type="button"
              onClick={() => canNext && setPanelYear((y) => y + 1)}
              disabled={!canNext}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-palette-gray-subtle text-palette-gray-secondary disabled:opacity-30"
            >
              ▶
            </button>
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-3 gap-1">
            {MONTHS.map((name, i) => {
              const m = i + 1;
              const disabled = isDisabled(panelYear, m);
              const active = multiple
                ? months.includes(`${panelYear}-${String(m).padStart(2, "0")}`)
                : selectedYear === panelYear && selectedMonth === m;
              return (
                <button
                  key={m}
                  type="button"
                  disabled={disabled}
                  onClick={() => select(m)}
                  className={`rounded-lg py-1.5 text-sm font-medium transition-colors
                    ${active ? "bg-palette-indigo-badge text-white" : ""}
                    ${!active && !disabled ? "hover:bg-palette-gray-subtle text-palette-gray-heading" : ""}
                    ${disabled ? "opacity-30 cursor-not-allowed text-palette-gray-secondary" : ""}
                  `}
                >
                  {name}
                </button>
              );
            })}
          </div>
          {multiple && (
            <p className="mt-2 text-[11px] text-palette-gray-secondary">
              Click months to add or remove them.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── DateRangeFilter ──────────────────────────────────────────────────────────

function fmtRangeDate(iso, withYear) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}${withYear ? `, ${y}` : ""}`;
}

/** ISO date + n days, via Date's own overflow normalisation. */
function addDaysISO(iso, n) {
  if (!iso) return iso;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(y, m - 1, d + n);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}

/**
 * Rolling N-day window: ◀ / dates / ▶, stepping a whole window at a time.
 *
 * Replaces the month picker on the cash-flow pages, where the useful question is
 * "the next 30 days" rather than "August". The step is the window length, so the
 * ranges tile without gaps or overlap. `Today` appears only once the window has
 * moved, since it would be a no-op otherwise.
 *
 * With `onStartChange`, the two ends become DATE PICKERS. The window length is
 * already fixed by the 30/60-day control, so the two ends are not independent —
 * picking either one determines the other, and only ONE value is actually stored
 * (the start). Picking an end date is just the start expressed differently:
 *     start = end − (days − 1)
 * That is why both inputs call the same `onStartChange`, rather than the caller
 * having to hold a second piece of state that could contradict the span.
 */
function DateRangeFilter({ start, end, days, onShift, onReset, isDefault, onStartChange }) {
  const sameYear = start?.slice(0, 4) === end?.slice(0, 4);
  const pickable = typeof onStartChange === "function";
  const dateInputCls =
    "h-7 bg-transparent text-sm font-medium text-palette-gray-heading outline-none " +
    "focus:ring-2 focus:ring-palette-indigo-label rounded px-1 cursor-pointer";
  return (
    <div>
      <FilterLabel>{days}-day window</FilterLabel>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onShift(-1)}
          title={`Previous ${days} days`}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-palette-gray-divider bg-white text-palette-gray-secondary transition-colors hover:bg-palette-gray-subtle"
        >
          ◀
        </button>
        {pickable ? (
          <div className="flex h-9 items-center gap-1 rounded-lg border border-palette-gray-divider bg-white px-2">
            <input
              type="date"
              value={start ?? ""}
              // Empty means the field was cleared mid-edit — ignore rather than
              // blanking the window, which would empty the whole grid.
              onChange={(e) => e.target.value && onStartChange(e.target.value)}
              title={`First day of the window — the last day follows, ${days} days later`}
              className={dateInputCls}
            />
            <span className="text-palette-gray-placeholder">–</span>
            <input
              type="date"
              value={end ?? ""}
              onChange={(e) =>
                e.target.value && onStartChange(addDaysISO(e.target.value, -(days - 1)))
              }
              title={`Last day of the window — the first day follows, ${days} days earlier`}
              className={dateInputCls}
            />
          </div>
        ) : (
          <div className="flex h-9 min-w-[200px] items-center justify-center rounded-lg border border-palette-gray-divider bg-white px-3 text-sm font-medium text-palette-gray-heading">
            {fmtRangeDate(start, !sameYear)} – {fmtRangeDate(end, true)}
          </div>
        )}
        <button
          type="button"
          onClick={() => onShift(1)}
          title={`Next ${days} days`}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-palette-gray-divider bg-white text-palette-gray-secondary transition-colors hover:bg-palette-gray-subtle"
        >
          ▶
        </button>
        {!isDefault && (
          <button
            type="button"
            onClick={onReset}
            title="Back to today"
            className="h-9 rounded-lg border border-palette-gray-divider bg-white px-3 text-sm font-medium text-palette-indigo-badge transition-colors hover:bg-palette-gray-subtle"
          >
            Today
          </button>
        )}
      </div>
    </div>
  );
}

// ─── DateSpanFilter ───────────────────────────────────────────────────────────

/**
 * Free from / to range: two date pickers, any length. Picking one end past
 * the other moves the other along, so `from` is never after `to`.
 */
function DateSpanFilter({ from, to, onChange, max, onReset, isDefault = true }) {
  const inputCls =
    "h-7 bg-transparent text-sm font-medium text-palette-gray-heading outline-none " +
    "focus:ring-2 focus:ring-palette-indigo-label rounded px-1 cursor-pointer";
  return (
    <div>
      <FilterLabel>Dates</FilterLabel>
      <div className="flex items-center gap-1">
        <div className="flex h-9 items-center gap-1 rounded-lg border border-palette-gray-divider bg-white px-2">
          <input
            type="date"
            value={from ?? ""}
            max={max}
            // Empty means the field was cleared mid-edit — ignore it.
            onChange={(e) => {
              const v = e.target.value;
              if (v) onChange({ from: v, to: to && to < v ? v : to });
            }}
            title="From"
            className={inputCls}
          />
          <span className="text-palette-gray-placeholder">–</span>
          <input
            type="date"
            value={to ?? ""}
            max={max}
            onChange={(e) => {
              const v = e.target.value;
              if (v) onChange({ from: from && from > v ? v : from, to: v });
            }}
            title="To"
            className={inputCls}
          />
        </div>
        {onReset && !isDefault && (
          <button
            type="button"
            onClick={onReset}
            title="Back to the default range, ending today"
            className="h-9 rounded-lg border border-palette-gray-divider bg-white px-3 text-sm font-medium text-palette-indigo-badge transition-colors hover:bg-palette-gray-subtle"
          >
            Today
          </button>
        )}
      </div>
    </div>
  );
}

// ─── DateFilter ───────────────────────────────────────────────────────────────

function DateFilter({ value, onChange, max, min }) {
  return (
    <div>
      <FilterLabel>Date</FilterLabel>
      <input
        type="date"
        value={value}
        max={max}
        min={min}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 px-3 rounded-lg border border-palette-gray-divider bg-white text-sm font-medium text-palette-gray-heading outline-none focus:ring-2 focus:ring-palette-indigo-label hover:bg-palette-gray-subtle transition-colors min-w-[160px]"
      />
    </div>
  );
}

// ─── AreaManagerFilter ────────────────────────────────────────────────────────

function AreaManagerFilter({ value, options, onChange }) {
  return (
    <div>
      <FilterLabel>Area Manager</FilterLabel>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 px-3 rounded-lg border border-palette-gray-divider bg-white text-sm font-medium text-palette-gray-heading outline-none focus:ring-2 focus:ring-palette-indigo-label hover:bg-palette-gray-subtle transition-colors min-w-[160px]"
      >
        <option value="">All area managers</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// ─── ClinicFilter ─────────────────────────────────────────────────────────────

// Multi-select picker with search. Defaults to clinic wording; the Dens N Dente
// page reuses it verbatim for entities by overriding the three labels, so both
// pickers behave identically (search, Select all, Clear, "N selected").
function ClinicFilter({
  options,
  value,
  onChange,
  label = "Clinic",
  allLabel = "All clinics",
  noun = "clinics",
  // One value at most: radio rows, no "Select all", and picking closes the list.
  single = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.name?.toLowerCase().includes(q) ||
        o.code?.toLowerCase().includes(q) ||
        o.subtitle?.toLowerCase().includes(q),
    );
  }, [options, query]);

  const triggerLabel = useMemo(() => {
    if (selectedSet.size === 0) return allLabel;
    if (selectedSet.size === 1) {
      const found = options.find((o) => selectedSet.has(o.value));
      return found?.name ?? `${selectedSet.size} selected`;
    }
    return `${selectedSet.size} selected`;
  }, [selectedSet, options, allLabel]);

  function toggle(v) {
    if (single) {
      onChange(selectedSet.has(v) ? [] : [v]);
      setOpen(false);
      return;
    }
    const next = new Set(selectedSet);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(Array.from(next));
  }

  function clear() {
    onChange([]);
  }

  function selectAll() {
    onChange(filtered.map((o) => o.value));
  }

  return (
    <div className="relative" ref={ref}>
      <FilterLabel>{label}</FilterLabel>
      <TriggerBtn onClick={() => setOpen((v) => !v)}>
        <span className="truncate">{triggerLabel}</span>
        <div className="flex items-center gap-1.5 shrink-0">
          {selectedSet.size === 0 && (
            <span className="text-xs text-palette-gray-secondary">{options.length} shown</span>
          )}
          <Caret open={open} />
        </div>
      </TriggerBtn>

      {open && (
        <div className="absolute z-50 mt-1 w-72 rounded-2xl border border-palette-gray-divider bg-white shadow-xl overflow-hidden">
          {/* Search */}
          <div className="p-3 border-b border-palette-gray-divider">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${options.length} ${noun}…`}
              className="w-full rounded-lg border border-palette-gray-divider px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-palette-indigo-label"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between px-4 py-1.5 text-xs border-b border-palette-gray-divider">
            {single ? (
              <span className="text-palette-gray-secondary">Pick one</span>
            ) : (
              <button
                type="button"
                onClick={selectAll}
                disabled={filtered.length === 0}
                className="text-palette-indigo-label hover:underline disabled:opacity-40"
              >
                Select all{query ? " (filtered)" : ""}
              </button>
            )}
            <button
              type="button"
              onClick={clear}
              disabled={selectedSet.size === 0}
              className="text-palette-gray-secondary hover:text-palette-gray-heading disabled:opacity-40"
            >
              Clear
            </button>
          </div>

          {/* List */}
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-sm text-palette-gray-placeholder text-center">
                No matches.
              </div>
            ) : (
              filtered.map((o) => {
                const checked = selectedSet.has(o.value);
                return (
                  <label
                    key={o.value}
                    className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${
                      checked ? "bg-palette-indigo-soft" : "hover:bg-palette-gray-subtle"
                    }`}
                  >
                    <input
                      type={single ? "radio" : "checkbox"}
                      className="h-4 w-4 rounded accent-palette-indigo-badge shrink-0"
                      checked={checked}
                      onChange={() => toggle(o.value)}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-palette-gray-primary truncate">
                        {o.name ?? o.label}
                      </div>
                      {o.subtitle && (
                        <div className="text-xs text-palette-gray-secondary truncate">
                          {o.subtitle}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ClinicFilterBar ──────────────────────────────────────────────────────────

/**
 * Compact filter bar with a Month or Date selector, plus Area Manager and
 * Clinic selectors.
 * clinicOptions items: { value, label, name, code, subtitle }
 *
 * Pass `dateValue`/`onDateChange` (instead of `months`/`onMonthsChange`) to
 * swap the Month picker for a single-day Date picker — e.g. Daily Bank
 * Balance filters by a specific day, not a calendar month.
 */
export default function ClinicFilterBar({
  months,
  onMonthsChange,
  disableCurrentAndFuture = false,
  // Lets the Month picker select several months (see MonthFilter).
  multipleMonths = false,

  dateValue,
  onDateChange,
  maxDate,
  minDate,

  // Rolling-window mode (cash flow): pass `onRangeShift` to swap the Month
  // picker for a ◀ / range / ▶ navigator that steps a whole window at a time.
  rangeStart,
  rangeEnd,
  rangeDays,
  onRangeShift,
  onRangeReset,
  rangeIsDefault = true,
  // Optional: makes both ends of the window date PICKERS instead of a static
  // label. Receives the new START date — picking the end is converted to a start
  // by the fixed window length, so there is still only one value to store.
  onRangeStartChange,

  // Free range mode: pass `onRangeChange` for plain from / to pickers of any
  // length. Receives { from, to }; `rangeMax` caps both (e.g. today).
  // `onRangeReset` / `rangeIsDefault` drive its Today button too.
  rangeFrom,
  rangeTo,
  rangeMax,
  onRangeChange,

  areaManagerFilter,
  areaManagerOptions,
  onAreaManagerChange,

  clinicOptions,
  clinicValue,
  onClinicChange,
  // Restrict the clinic picker to ONE clinic — for views too heavy to run
  // across many clinics at once (Daily Deposits).
  singleClinic = false,

  // Entity mode (Dens N Dente): pass `onEntityChange` to swap the multi-select
  // clinic picker for a single-select entity picker. Area Manager is hidden by
  // default in this mode — an entity has no area manager — but can be forced
  // back with `showAreaManager`.
  entityOptions,
  entityValue,
  onEntityChange,
  showAreaManager,
  // Set false to render neither picker — e.g. a tab whose host page already owns
  // the entity selection and only needs the date here.
  showClinic = true,

  // Optional third picker, rendered after the clinic/entity one: narrows to
  // specific bank accounts. Only appears when `onAccountChange` is supplied, so
  // every existing caller is unaffected.
  accountOptions,
  accountValue,
  onAccountChange,

  // Anything the page wants to sit at the END of the bar, on the same baseline
  // as the pickers — a freshness/refresh control, say. A slot rather than a
  // prop per control: the bar has no business knowing what a caller needs
  // beside its selectors.
  trailing,
}) {
  const entityMode = typeof onEntityChange === "function";
  const areaManagerVisible = showAreaManager ?? !entityMode;

  return (
    <div className="flex flex-wrap items-end gap-4 mb-6">
      {typeof onRangeChange === "function" ? (
        <DateSpanFilter
          from={rangeFrom}
          to={rangeTo}
          max={rangeMax}
          onChange={onRangeChange}
          onReset={onRangeReset}
          isDefault={rangeIsDefault}
        />
      ) : typeof onRangeShift === "function" ? (
        <DateRangeFilter
          start={rangeStart}
          end={rangeEnd}
          days={rangeDays}
          onShift={onRangeShift}
          onReset={onRangeReset}
          isDefault={rangeIsDefault}
          onStartChange={onRangeStartChange}
        />
      ) : onDateChange ? (
        <DateFilter value={dateValue} onChange={onDateChange} max={maxDate} min={minDate} />
      ) : (
        <MonthFilter
          months={months}
          onMonthsChange={onMonthsChange}
          disableCurrentAndFuture={disableCurrentAndFuture}
          multiple={multipleMonths}
        />
      )}
      {areaManagerVisible && (
        <AreaManagerFilter
          value={areaManagerFilter}
          options={areaManagerOptions}
          onChange={onAreaManagerChange}
        />
      )}
      {showClinic &&
        (entityMode ? (
          // Same multi-select component as clinics — entities are selected the
          // same way (search, Select all, Clear, "N selected"), just relabelled.
          <ClinicFilter
            options={entityOptions ?? []}
            value={entityValue ?? []}
            onChange={onEntityChange}
            label="Entity"
            allLabel="All entities"
            noun="entities"
          />
        ) : (
          <ClinicFilter
            options={clinicOptions}
            value={singleClinic ? (clinicValue ?? []).slice(0, 1) : clinicValue}
            onChange={onClinicChange}
            single={singleClinic}
            allLabel={singleClinic ? "Select a clinic" : "All clinics"}
          />
        ))}
      {typeof onAccountChange === "function" && (
        <ClinicFilter
          options={accountOptions ?? []}
          value={accountValue ?? []}
          onChange={onAccountChange}
          label="Accounts"
          allLabel="All accounts"
          noun="accounts"
        />
      )}
      {trailing}
    </div>
  );
}
